/**
 * RunTrace — the per-step trajectory of one worker run, rendered as a vertical
 * waterfall. The raw material the optimizer reads (run_steps), made visible to
 * the human: "trajectory, not just outcome" (UI-SPEC §5, PRD §4.3).
 *
 * Loop Runtime PRD §5.6 added two rendering rules:
 *   1. Steps sharing a `parallel_group_key` render as a single bordered group
 *      with sibling sub-rows (one per parallel branch).
 *   2. Retries (rows with `retry_of_step_id != NULL`) render indented under
 *      their root step, badged `attempt N/maxAttempts`.
 *
 * Presentational only — the drawer open/close state lives in the caller (the
 * /agents client island wraps this in an <EntityDrawer>).
 */

import * as React from "react";
import { Card } from "../../card";
import { formatUsd } from "../../format";
import { IntentBadge } from "../status-badge";
import { RungBadge } from "./rung-badge";

export interface RunTraceStep {
  id: number;
  ordinal: number;
  rung: number;
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cached_tokens: number | null;
  wall_ms: number | null;
  cost_usd: number | null;
  tool_calls: number | null;
  output_excerpt: string | null;
  reflection: string | null;
  // v44 — per-attempt audit fields. Optional so older callers compile.
  attempt?: number;
  parent_step_id?: number | null;
  retry_of_step_id?: number | null;
  parallel_group_key?: string | null;
}

function fmtTokens(n: number | null): string {
  if (n === null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function fmtMs(n: number | null): string {
  if (n === null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}s`;
  return `${n}ms`;
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="type-subtext text-muted-foreground">{label}</span>
      <span className="type-body tabular-nums text-foreground">{value}</span>
    </div>
  );
}

interface OrderedGroup {
  kind: "single" | "parallel";
  ordinal: number;
  parallelGroupKey?: string;
  // Attempts grouped by their root step id. attempts[0] is attempt 1.
  // For parallel groups, each sibling owns its own attempts list.
  branches: Array<{
    rootId: number;
    attempts: RunTraceStep[];
  }>;
}

/**
 * Bucket the flat step list into render groups:
 *  - Steps with a `retry_of_step_id` set are folded into their root step's
 *    `attempts` list (sorted by attempt asc).
 *  - Steps sharing a `parallel_group_key` (and ordinal) at the same rung
 *    collapse into one parallel group containing N branches.
 *  - Everything else is a single-branch group of one.
 */
function groupSteps(steps: RunTraceStep[]): OrderedGroup[] {
  if (steps.length === 0) return [];
  const byId = new Map<number, RunTraceStep>();
  for (const s of steps) byId.set(s.id, s);

  // For each step, find its "root" id — the id of attempt 1 of its branch.
  const rootIdOf = (s: RunTraceStep): number => {
    if (!s.retry_of_step_id) return s.id;
    return s.retry_of_step_id;
  };

  // Group attempts by root id, preserving root-step order.
  const branchByRoot = new Map<number, RunTraceStep[]>();
  for (const s of steps) {
    const root = rootIdOf(s);
    const arr = branchByRoot.get(root) ?? [];
    arr.push(s);
    branchByRoot.set(root, arr);
  }
  for (const arr of branchByRoot.values()) {
    arr.sort((a, b) => (a.attempt ?? 1) - (b.attempt ?? 1));
  }

  // Walk the unique root steps in (ordinal, id) order to derive top-level
  // groups. Roots that share (ordinal, parallel_group_key) collapse into a
  // single parallel group.
  const rootSteps = [...branchByRoot.keys()]
    .map((id) => byId.get(id)!)
    .sort(
      (a, b) =>
        a.ordinal - b.ordinal || (a.attempt ?? 1) - (b.attempt ?? 1) || a.id - b.id,
    );

  const groups: OrderedGroup[] = [];
  let i = 0;
  while (i < rootSteps.length) {
    const head = rootSteps[i]!;
    const groupKey = head.parallel_group_key ?? null;
    if (!groupKey) {
      groups.push({
        kind: "single",
        ordinal: head.ordinal,
        branches: [{ rootId: head.id, attempts: branchByRoot.get(head.id)! }],
      });
      i++;
      continue;
    }
    // Collect consecutive siblings at the same ordinal + group key.
    const siblings: RunTraceStep[] = [head];
    let j = i + 1;
    while (
      j < rootSteps.length &&
      rootSteps[j]!.ordinal === head.ordinal &&
      rootSteps[j]!.parallel_group_key === groupKey
    ) {
      siblings.push(rootSteps[j]!);
      j++;
    }
    groups.push({
      kind: "parallel",
      ordinal: head.ordinal,
      parallelGroupKey: groupKey,
      branches: siblings.map((s) => ({
        rootId: s.id,
        attempts: branchByRoot.get(s.id)!,
      })),
    });
    i = j;
  }
  return groups;
}

export function RunTrace({ steps }: { steps: RunTraceStep[] }) {
  if (steps.length === 0) {
    return (
      <p className="type-body text-muted-foreground">
        No per-step trace recorded for this run.
      </p>
    );
  }
  const groups = groupSteps(steps);
  return (
    <div className="flex flex-col gap-3">
      {groups.map((g, gi) => (
        <RunTraceGroup key={gi} group={g} />
      ))}
    </div>
  );
}

function RunTraceGroup({ group }: { group: OrderedGroup }) {
  if (group.kind === "single") {
    const branch = group.branches[0]!;
    return <BranchCard ordinal={group.ordinal} attempts={branch.attempts} />;
  }
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border/60 p-2">
      <div className="flex items-center justify-between px-1">
        <span className="type-subtext text-muted-foreground tabular-nums">
          #{group.ordinal}
        </span>
        <span className="type-subtext text-muted-foreground">
          parallel · {group.branches.length} sibling
          {group.branches.length === 1 ? "" : "s"}
          {group.parallelGroupKey ? ` · ${group.parallelGroupKey}` : ""}
        </span>
      </div>
      <div className="flex flex-col gap-2 pl-2">
        {group.branches.map((b, bi) => (
          <BranchCard key={b.rootId} ordinal={group.ordinal} attempts={b.attempts} sibling={bi + 1} />
        ))}
      </div>
    </div>
  );
}

function BranchCard({
  ordinal,
  attempts,
  sibling,
}: {
  ordinal: number;
  attempts: RunTraceStep[];
  sibling?: number;
}) {
  const [first, ...retries] = attempts;
  return (
    <Card className="flex flex-col gap-3 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="type-subtext text-muted-foreground tabular-nums">
            #{ordinal}
            {sibling !== undefined ? `.${sibling}` : ""}
          </span>
          <RungBadge rung={first!.rung} />
          {attempts.length > 1 ? (
            <IntentBadge intent="neutral">{attempts.length} attempts</IntentBadge>
          ) : null}
        </div>
        <span className="font-mono type-subtext text-muted-foreground">
          {first!.model ?? "—"}
        </span>
      </div>
      <StepRow step={first!} attemptCount={attempts.length} />
      {retries.length > 0 ? (
        <div className="flex flex-col gap-2 border-l-2 border-border/60 pl-3">
          {retries.map((r) => (
            <StepRow key={r.id} step={r} attemptCount={attempts.length} retry />
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function StepRow({
  step,
  attemptCount,
  retry,
}: {
  step: RunTraceStep;
  attemptCount: number;
  retry?: boolean;
}) {
  const attemptN = step.attempt ?? 1;
  return (
    <div className="flex flex-col gap-2">
      {retry || attemptCount > 1 ? (
        <div className="flex items-center gap-2">
          <IntentBadge intent="neutral" className="tabular-nums">
            attempt {attemptN}/{attemptCount}
          </IntentBadge>
        </div>
      ) : null}
      <div className="grid grid-cols-3 gap-2">
        <Metric
          label="tokens (in/out)"
          value={`${fmtTokens(step.input_tokens)} / ${fmtTokens(step.output_tokens)}`}
        />
        <Metric label="cached" value={fmtTokens(step.cached_tokens)} />
        <Metric label="cost" value={formatUsd(step.cost_usd)} />
        <Metric label="latency" value={fmtMs(step.wall_ms)} />
        <Metric
          label="tool calls"
          value={step.tool_calls === null ? "—" : String(step.tool_calls)}
        />
      </div>
      {step.output_excerpt ? (
        <div className="flex flex-col gap-1">
          <span className="type-subtext text-muted-foreground">output</span>
          <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded bg-selected px-2 py-1 type-subtext text-foreground">
            {step.output_excerpt}
          </pre>
        </div>
      ) : null}
      {step.reflection ? (
        <div className="flex flex-col gap-1">
          <span className="type-subtext text-muted-foreground">
            optimizer reflection
          </span>
          <p className="type-body text-foreground">{step.reflection}</p>
        </div>
      ) : null}
    </div>
  );
}
